import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { regexNonEmptyString } from "../../common/regex";
import { validatePassword } from "../../common/utils";

/**
 * Defines possible user statuses.
 */
export enum UserStatus {
	Disabled = 0,
	Active = 1,
	Deleted = 2,
	ApprovalRequired = 3,
}

/**
 * Defines the payload used to create or update users.
 */
export type UpsertUserPayload = {
	firstName: string;
	lastName: string;
	username: string;
	email: string;
	telephone: string;
	roleId: string;
	preferredLanguage: string;
	password?: string;
	passwordConfirm?: string;
	status: UserStatus;
	blocked?: boolean;
	multiFactorAuthenticationEnabled?: boolean;
	expiredAt?: Dayjs;
};

/**
 * Defines an empty UpsertUserPayload.
 */
export const emptyUpsertUserPayload: UpsertUserPayload = {
	firstName: "",
	lastName: "",
	username: "",
	email: "",
	telephone: "",
	roleId: "",
	preferredLanguage: "",
	password: "",
	passwordConfirm: "",
	status: UserStatus.Active,
};

/**
 * Returns validation schemas used to validate users payloads.
 */
export const useUserValidationSchemas = () => {
	const { t } = useTranslation("validation");

	const UpsertUserSchema = z
		.object({
			firstName: z.string().regex(regexNonEmptyString, t("common/required")),
			lastName: z.string().regex(regexNonEmptyString, t("common/required")),
			username: z.string().regex(regexNonEmptyString, t("common/required")),
			email: z.string().regex(regexNonEmptyString, t("common/required")).email(t("common/invalid email")),
			telephone: z.string().regex(regexNonEmptyString, t("common/required")),
			roleId: z.string().regex(regexNonEmptyString, t("common/required")),
			preferredLanguage: z.string(), // Is this field required?
			password: z.string().optional(),
			passwordConfirm: z.string().optional(),
			status: z.number({ required_error: t("common/required") }),
			blocked: z.boolean().optional(),
			multiFactorAuthenticationEnabled: z.boolean().optional(),
			expiredAt: z
				.union([z.custom<Dayjs>((v) => dayjs.isDayjs(v), t("common/invalid date")), z.literal(undefined)])
				.optional(),
		})
		.superRefine((data, ctx) => {
			const issues = validatePassword(data.password ?? "", data.passwordConfirm ?? "", t);

			for (const issue of issues) {
				ctx.addIssue(issue);
			}
		});

	return { UpsertUserSchema };
};
